import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface BookModeProps {
  content: string;
  title: string;
  currentPage: number;
  totalPages: number;
  handlePageTurn: (direction: 'next' | 'prev') => void;
  leftPage: { isTitlePage: boolean; content: string };
  rightPage: { content: string };
  themeColors: any;
  isDark: boolean;
  reducedMotion: boolean;
  isMobile?: boolean;
  activeNovel?: any;
  chapterId: string;
}

export function BookMode({
  content,
  title,
  currentPage,
  totalPages,
  handlePageTurn,
  leftPage,
  rightPage,
  themeColors,
  isDark,
  reducedMotion,
  isMobile,
  activeNovel,
  chapterId
}: BookModeProps) {
  const pageVariants = {
    initial: { opacity: 0, x: reducedMotion ? 0 : 20 },
    animate: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: reducedMotion ? 0 : -20 },
  };

  const pageTransition = {
    duration: reducedMotion ? 0.1 : 0.4,
    ease: [0.4, 0, 0.2, 1] as const,
  };

  if (isMobile) {
    return (
      <div
        className="h-full overflow-y-auto px-4 py-4"
        style={{ backgroundColor: themeColors.bookBg }}
      >
        <div
          className="min-h-full p-6 rounded-lg"
          style={{
            backgroundColor: themeColors.pageBg,
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}
        >
          <div className="text-center mb-6">
            <h1 className="text-xl font-serif mb-2" style={{ color: themeColors.text }}>
              {title}
            </h1>
            <div className="w-12 h-px mx-auto" style={{ backgroundColor: themeColors.border }} />
          </div>

          <div
            className="w-full h-full overflow-y-auto outline-none bg-transparent font-serif text-base leading-relaxed editor-richtext"
            style={{ color: themeColors.text, lineHeight: '1.8', minHeight: '60vh' }}
            dangerouslySetInnerHTML={{ __html: content }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col items-center justify-center p-4 md:p-8">
      <div className="relative flex w-full max-w-[1200px] gap-0 shadow-2xl">
        {/* Left Page */}
        <div
          className="flex-1 min-h-[60vh] md:min-h-[70vh] p-6 md:p-10 relative overflow-hidden"
          style={{
            backgroundColor: themeColors.pageBg,
            backgroundImage: isDark
              ? 'linear-gradient(to right, rgba(0,0,0,0.3) 0%, transparent 8%)'
              : 'linear-gradient(to right, rgba(0,0,0,0.05) 0%, transparent 5%)',
            borderRadius: '4px 0 0 4px',
            boxShadow: isDark
              ? 'inset -10px 0 20px rgba(0,0,0,0.4), -5px 5px 15px rgba(0,0,0,0.3)'
              : 'inset -10px 0 20px rgba(0,0,0,0.08), -5px 5px 15px rgba(0,0,0,0.1)',
          }}
        >
          <AnimatePresence mode="wait">
            <motion.div
              key={`left-${currentPage}-${chapterId}`}
              initial={pageVariants.initial}
              animate={pageVariants.animate}
              exit={pageVariants.exit}
              transition={pageTransition}
              className="h-full"
            >
              {leftPage.isTitlePage ? (
                <div className="h-full flex flex-col items-center justify-center text-center">
                  <h1
                    className="text-2xl md:text-3xl mb-4"
                    style={{ color: themeColors.text, fontFamily: '"Crimson Text", Georgia, serif' }}
                  >
                    {title}
                  </h1>
                  <div className="w-16 h-px mb-4" style={{ backgroundColor: themeColors.border }} />
                  {activeNovel && (
                    <p
                      className="text-lg italic"
                      style={{ color: themeColors.textMuted, fontFamily: '"Crimson Text", Georgia, serif' }}
                    >
                      {activeNovel.title}
                    </p>
                  )}
                </div>
              ) : (
                <div
                  className="w-full h-full overflow-y-auto outline-none bg-transparent text-base md:text-lg leading-relaxed editor-richtext"
                  style={{ color: themeColors.text, lineHeight: '1.8', letterSpacing: '0.01em', minHeight: '50vh', fontFamily: '"Crimson Text", Georgia, serif' }}
                  dangerouslySetInnerHTML={{ __html: leftPage.content }}
                />
              )}
            </motion.div>
          </AnimatePresence>
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-sm" style={{ color: themeColors.textMuted, fontFamily: '"Crimson Text", Georgia, serif' }}>
            {currentPage === 0 ? 1 : currentPage * 2}
          </div>
        </div>

        {/* Right Page */}
        <div
          className="flex-1 min-h-[60vh] md:min-h-[70vh] p-6 md:p-10 relative overflow-hidden"
          style={{
            backgroundColor: themeColors.pageBg,
            backgroundImage: isDark
              ? 'linear-gradient(to left, rgba(0,0,0,0.2) 0%, transparent 8%)'
              : 'linear-gradient(to left, rgba(0,0,0,0.03) 0%, transparent 5%)',
            borderRadius: '0 4px 4px 0',
            boxShadow: isDark
              ? 'inset 10px 0 20px rgba(0,0,0,0.4), 5px 5px 15px rgba(0,0,0,0.3)'
              : 'inset 10px 0 20px rgba(0,0,0,0.08), 5px 5px 15px rgba(0,0,0,0.1)',
          }}
        >
          <AnimatePresence mode="wait">
            <motion.div
              key={`right-${currentPage}-${chapterId}`}
              initial={pageVariants.initial}
              animate={pageVariants.animate}
              exit={pageVariants.exit}
              transition={pageTransition}
              className="h-full"
            >
              <div
                className="w-full h-full overflow-y-auto outline-none bg-transparent text-base md:text-lg leading-relaxed editor-richtext"
                style={{ color: themeColors.text, lineHeight: '1.8', letterSpacing: '0.01em', minHeight: '50vh', fontFamily: '"Crimson Text", Georgia, serif' }}
                dangerouslySetInnerHTML={{ __html: rightPage.content }}
              />
            </motion.div>
          </AnimatePresence>
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-sm" style={{ color: themeColors.textMuted, fontFamily: '"Crimson Text", Georgia, serif' }}>
            {currentPage * 2 + (leftPage.isTitlePage ? 2 : 1)}
          </div>
        </div>

        {/* Page Turn Controls */}
        <button
          onClick={() => handlePageTurn('prev')}
          disabled={currentPage === 0}
          className="absolute -left-12 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-0 transition-all"
        >
          <ChevronLeft className="w-6 h-6 text-white" />
        </button>
        <button
          onClick={() => handlePageTurn('next')}
          disabled={currentPage >= totalPages - 1}
          className="absolute -right-12 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-0 transition-all"
        >
          <ChevronRight className="w-6 h-6 text-white" />
        </button>
      </div>
    </div>
  );
}
